/**
 * FactoringOperationsTab — TES-02/03: Operaciones de Factoring
 */
import { todayISO } from '../../../lib/utils'
import { useEffect, useState } from 'react'
import DatePicker from '@/components/DatePicker'
import { usePermission } from '@/hooks/usePermission'
import { fmtDate, formatRut } from '@/lib/utils'
import { useTenantLocale } from '@/lib/locale'
import {
  Plus,
  Loader2,
  FileText,
  Send,
  CreditCard,
  Search,
  X,
  FileCode,
  Download,
  Copy,
  Check,
  Clock,
  CheckCircle2,
  XCircle,
  Minus,
  RefreshCw,
} from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import apiClient from '@/lib/apiClient'
import {
  useFactoringOperations, useFactoringOperation, useCreateFactoringOperation,
  useSubmitFactoringOperation, useFactoringEntities, useSendCesion, useRecordSiiResult,
  useConsultarCesionEnSii,
  type FactoringOperationListItem, type FactoringOperationStatus,
  type FactoringOperation, type FactoringOperationLine,
  type CesionStatus, type FactoringOperationDocument,
} from '../hooks/useTreasury'
import { RegisterPaymentModal } from './RegisterPaymentModal'
import { useSaleDocuments } from '@/modules/sales/hooks/useSales'
import { Pagination } from '@/components/ds/Pagination'
import { Modal } from '@/components/ds/Modal'

// Etiqueta legible del documento cedido: "FE 6532" (folio SII), con fallback al id.
function docLabel(line: FactoringOperationLine): string {
  const num = line.sale_document_number
  if (num) return line.sale_document_type ? `${line.sale_document_type} ${num}` : num
  return line.sale_document.slice(0, 8)
}

// ─── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<FactoringOperationStatus, { label: string; className: string }> = {
  created:    { label: 'Creada',      className: 'bg-surface-muted text-content-muted border-divider' },
  processing: { label: 'Procesando',  className: 'bg-warning-surface text-warning-content border-warning-border' },
  accepted:   { label: 'Cedida',      className: 'bg-primary-50 text-primary-hover border-primary-200' },
  rejected:   { label: 'Rechazada',   className: 'bg-danger-surface text-danger-content border-danger-border' },
  paid:       { label: 'Liquidada',   className: 'bg-success-surface text-success-content border-success-border' },
}

function StatusBadge({ status }: { status: FactoringOperationStatus }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, className: 'bg-surface-muted text-content-muted border-divider' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

// ─── Estado de la cesión ante el SII ──────────────────────────────────────────
// Eje separado de `status`: "Cedida" describe la operación comercial y no dice
// nada de lo que respondió el RPETC. Mostrarlos juntos evita el caso del
// 2026-08-11, en que dos cesiones rechazadas por el SII se veían como "Cedida".

const CESION_CONFIG: Record<CesionStatus, { label: string; icon: typeof CheckCircle2; className: string }> = {
  not_sent: {
    label: 'Sin enviar',
    icon: Minus,
    className: 'bg-surface-muted text-content-muted border-divider',
  },
  pending: {
    label: 'Esperando al SII',
    icon: Clock,
    className: 'bg-warning-surface text-warning-content border-warning-border',
  },
  accepted: {
    label: 'Aceptada',
    icon: CheckCircle2,
    className: 'bg-success-surface text-success-content border-success-border',
  },
  rejected: {
    label: 'Rechazada',
    icon: XCircle,
    className: 'bg-danger-surface text-danger-content border-danger-border',
  },
}

function CesionBadge({ status, label, trackId }: {
  status: CesionStatus
  label?: string
  trackId?: string
}) {
  const cfg = CESION_CONFIG[status] ?? CESION_CONFIG.not_sent
  const Icon = cfg.icon
  return (
    <span
      title={trackId ? `${label ?? cfg.label} · Envío ${trackId}` : (label ?? cfg.label)}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.className}`}
    >
      <Icon size={11} />
      {cfg.label}
    </span>
  )
}

/** Documentos cedidos, resumidos: "FE 6624 · CMPC". Con más de dos, se corta. */
function DocumentsCell({ documents, lineCount }: {
  documents: FactoringOperationDocument[]
  lineCount: number
}) {
  if (!documents || documents.length === 0) {
    return (
      <span className="text-xs text-content-subtle">
        {lineCount} doc{lineCount !== 1 ? 's' : ''}
      </span>
    )
  }
  const visibles = documents.slice(0, 2)
  const resto = documents.length - visibles.length
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      {visibles.map((d, i) => (
        <span key={i} className="text-xs text-content truncate">
          <span className="font-mono font-medium">
            {d.document_type} {d.folio ?? '—'}
          </span>
          {d.customer_name && (
            <span className="text-content-muted"> · {d.customer_name}</span>
          )}
        </span>
      ))}
      {resto > 0 && (
        <span className="text-[11px] text-content-subtle">y {resto} más</span>
      )}
    </div>
  )
}

function fmtNum(value: string) {
  const n = parseFloat(value)
  if (isNaN(n)) return value
  return n.toLocaleString('es-CL', { maximumFractionDigits: 0 })
}

// ─── Create operation modal ────────────────────────────────────────────────────

function CreateOperationModal({ onClose }: { onClose: () => void }) {
  const create = useCreateFactoringOperation()
  const { data: entitiesData } = useFactoringEntities({ is_active: 'true' })
  const entities = entitiesData?.entities ?? []

  const [docSearch, setDocSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(docSearch.trim()), 300)
    return () => clearTimeout(t)
  }, [docSearch])

  const { data: docsData, isFetching: docsLoading } = useSaleDocuments({
    status: 'accepted',
    search: debouncedSearch || undefined,
    page_size: 200,
  })
  const acceptedDocs = docsData?.documents ?? []

  const today = todayISO()

  const [entityId, setEntityId] = useState('')
  const [date, setDate] = useState(today)
  const [notes, setNotes] = useState('')
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  const toggleDoc = (id: string) =>
    setSelectedDocIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!entityId || selectedDocIds.length === 0) return
    setErrors({})
    try {
      // Get amounts from selected docs
      const lines = selectedDocIds.map(docId => {
        const doc = acceptedDocs.find(d => d.id === docId)
        return {
          sale_document: docId,
          amount_ceded: doc?.total ?? '0',
        }
      })
      await create.mutateAsync({ factoring_entity: entityId, date, notes, lines })
      onClose()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { details?: Record<string, string[]>; message?: string } } } }
      const details = e.response?.data?.error?.details
      const message = e.response?.data?.error?.message
      if (details) {
        const mapped: Record<string, string> = {}
        Object.entries(details).forEach(([k, v]) => { mapped[k] = Array.isArray(v) ? v[0] : String(v) })
        setErrors(mapped)
      } else if (message) {
        setErrors({ _: message })
      }
    }
  }

  return (
    <Modal open onClose={onClose} title="Nueva Operación de Factoring" size="2xl">
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {errors._ && (
            <div className="p-3 bg-danger-surface border border-danger-border rounded-lg text-xs text-danger-content-soft">{errors._}</div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="factoringoperationstab-entidad-de-factoring" className="block text-xs font-medium text-content-muted mb-1">Entidad de Factoring *</label>
              <select id="factoringoperationstab-entidad-de-factoring"
                value={entityId}
                onChange={e => setEntityId(e.target.value)}
                required
                className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
              >
                <option value="">Seleccionar...</option>
                {entities.map(en => (
                  <option key={en.id} value={en.id}>{en.legal_name} ({formatRut(en.tax_id)})</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="factoringoperationstab-fecha" className="block text-xs font-medium text-content-muted mb-1">Fecha *</label>
              <DatePicker id="factoringoperationstab-fecha" value={date} onChange={setDate} required />
            </div>
            <div className="col-span-2">
              <label htmlFor="factoringoperationstab-notas" className="block text-xs font-medium text-content-muted mb-1">Notas</label>
              <textarea id="factoringoperationstab-notas"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-content-muted mb-2">
              Documentos a Ceder * ({selectedDocIds.length} seleccionados)
            </label>

            {/* Buscador: folio, cliente o monto (server-side) */}
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
              <input aria-label="Buscar por folio, cliente o monto"
                type="text"
                value={docSearch}
                onChange={e => setDocSearch(e.target.value)}
                placeholder="Buscar por folio, cliente o monto…"
                className="w-full border border-divider rounded-lg pl-8 pr-8 py-2 text-sm focus:ring-2 focus:ring-primary"
              />
              {docSearch && (
                <button
                  type="button"
                  onClick={() => setDocSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-muted"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {docsLoading && acceptedDocs.length === 0 ? (
              <p className="text-xs text-content-subtle text-center py-6 border border-dashed border-divider rounded-lg flex items-center justify-center gap-2">
                <Loader2 size={13} className="animate-spin" /> Buscando…
              </p>
            ) : acceptedDocs.length === 0 ? (
              <p className="text-xs text-content-subtle text-center py-6 border border-dashed border-divider rounded-lg">
                {debouncedSearch
                  ? `Sin resultados para "${debouncedSearch}"`
                  : 'No hay facturas aceptadas disponibles para ceder'}
              </p>
            ) : (
              <div className="border border-divider rounded-lg overflow-hidden max-h-56 overflow-y-auto scrollbar-slim">
                {acceptedDocs.map(doc => (
                  <label
                    key={doc.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-soft cursor-pointer border-b border-divider last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDocIds.includes(doc.id)}
                      onChange={() => toggleDoc(doc.id)}
                      className="rounded"
                    />
                    <span className="text-xs font-mono text-content w-24 shrink-0">
                      {doc.document_type_display || doc.document_type} {doc.folio || doc.id.slice(0, 8)}
                    </span>
                    <span className="text-xs text-content-muted flex-1 truncate">{doc.customer_name}</span>
                    <span className="text-xs font-medium text-content">
                      {fmtNum(doc.total)}
                    </span>
                  </label>
                ))}
              </div>
            )}
            {acceptedDocs.length >= 200 && (
              <p className="text-[11px] text-content-subtle mt-1">
                Mostrando los primeros 200. Afina la búsqueda para encontrar un documento específico.
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-divider shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 border border-divider rounded-lg text-sm text-content-muted hover:bg-soft">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={create.isPending || !entityId || selectedDocIds.length === 0}
            className="flex-1 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-hover disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {create.isPending && <Loader2 size={13} className="animate-spin" />}
            Crear Operación
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ─── AEC (XML de cesión) viewer ──────────────────────────────────────────────

function AecViewerModal({ op, onClose }: { op: FactoringOperation; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const xml = op.cesion_xml ?? ''

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(xml)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard no disponible */ }
  }

  const handleDownload = () => {
    const blob = new Blob([xml], { type: 'application/xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `AEC_${(op.entity_name || 'cesion').replace(/\s+/g, '_')}_${op.date}.xml`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Modal open onClose={onClose} title={<span className="flex items-center gap-2"><FileCode size={16} className="text-primary" />XML de Cesión (AEC){op.cesion_signed ? ' — firmado' : ' — sin firmar'}</span>} size="2xl">
      {op.cesion_signed ? (
        <div className="px-6 py-2 border-b border-divider shrink-0 text-[11px] text-content-muted bg-soft">
          AEC firmado con el certificado de la empresa — válido para el RPETC del SII.
          Puedes enviarlo con «Enviar al SII» o descargarlo para subirlo al portal.
        </div>
      ) : (
        <div className="px-6 py-2 border-b border-divider shrink-0 text-[11px] text-warning-content bg-warning-surface">
          AEC sin firmar — todavía no es válido para el SII (falta certificado digital).
        </div>
      )}
      <div className="py-2 border-b border-divider shrink-0 flex items-center gap-2">
        <button onClick={handleCopy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-divider rounded-lg text-xs text-content-muted hover:bg-soft">
          {copied ? <Check size={12} /> : <Copy size={12} />}{copied ? 'Copiado' : 'Copiar'}
        </button>
        <button onClick={handleDownload}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-divider rounded-lg text-xs text-content-muted hover:bg-soft">
          <Download size={12} /> Descargar
        </button>
      </div>
      <div className="overflow-auto flex-1 min-h-0">
        <pre className="text-[11px] font-mono text-content whitespace-pre-wrap break-all scrollbar-slim">{xml}</pre>
      </div>
    </Modal>
  )
}

// ─── Registrar resultado del SII (RPETCMAIL) ─────────────────────────────────

function SiiResultModal({ operationId, onClose }: { operationId: string; onClose: () => void }) {
  const record = useRecordSiiResult()
  const [raw, setRaw] = useState('')

  const handleSave = async () => {
    if (!raw.trim()) return
    await record.mutateAsync({ id: operationId, raw })
    onClose()
  }

  return (
    <Modal open onClose={onClose} title="Registrar resultado del SII (RPETCMAIL)" size="xl"
      actions={<>
        <button type="button" onClick={onClose}
          className="flex-1 px-4 py-2 border border-divider rounded-lg text-sm text-content-muted hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={record.isPending || !raw.trim()}
          className="flex-1 px-4 py-2 bg-primary text-on-primary rounded-lg text-sm hover:bg-primary-hover disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {record.isPending && <Loader2 size={13} className="animate-spin" />}
          Registrar
        </button>
      </>}
    >
      <div className="flex-1 min-h-0 overflow-y-auto">
        <p className="text-xs text-content-muted mb-2">
          Pega el correo «Registro Público Electrónico de Transferencia de Crédito» que envió el SII.
          Se extraen el estado, la fecha de recepción y el identificador de cesión.
        </p>
        <textarea aria-label="Correo del Registro de Transferencia de Crédito"
          value={raw}
          onChange={e => setRaw(e.target.value)}
          rows={12}
          className="w-full border border-divider rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-primary scrollbar-slim"
          placeholder="Registro Público Electrónico de Transferencia de Crédito&#10;..."
        />
      </div>
    </Modal>
  )
}

// ─── Operation detail modal ────────────────────────────────────────────────────
// El detalle vive en un modal y no en un acordeón: el listado es una tabla que
// crece con el tiempo, y expandir filas empujaba el resto de las operaciones
// fuera de la pantalla.

function OperationDetailModal({ operationId, onClose }: {
  operationId: string
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Detalle de la operación de factoring"
        onClick={e => e.stopPropagation()}
        className="bg-surface rounded-2xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col"
      >
        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
          <OperationDetailPanel operationId={operationId} onClose={onClose} />
        </div>
      </div>
    </div>
  )
}

// ─── Operation detail panel ────────────────────────────────────────────────────

function OperationDetailPanel({
  operationId,
  onClose,
}: {
  operationId: string
  onClose: () => void
}) {
  const locale = useTenantLocale()
  const { data: op, isLoading, refetch } = useFactoringOperation(operationId)
  const submit = useSubmitFactoringOperation()
  const sendCesion = useSendCesion()
  const [showPayment, setShowPayment] = useState(false)
  const [showAec, setShowAec] = useState(false)
  const [showSiiResult, setShowSiiResult] = useState(false)
  const consultarSii = useConsultarCesionEnSii()
  const [siiMsg, setSiiMsg] = useState<{ ok: boolean; texto: string } | null>(null)
  const [bajandoCert, setBajandoCert] = useState(false)
  const [certError, setCertError] = useState<string | null>(null)

  const handleSendCesion = async () => {
    await sendCesion.mutateAsync({ id: operationId })
    await refetch()
  }

  // Consulta al Registro del SII. Es la vía confiable: el correo del SII puede
  // no llegar. La misma consulta corre sola 10 min después del envío.
  const handleConsultarSii = async () => {
    setSiiMsg(null)
    try {
      const r = await consultarSii.mutateAsync(operationId)
      await refetch()
      // `throttled`: el backend respondió con lo consultado hace menos de un
      // minuto. Se dice, en vez de simular una consulta nueva.
      const prefijo = r.throttled ? 'Consultado hace instantes: ' : ''
      setSiiMsg(
        r.encontradas > 0
          ? { ok: true, texto: `${prefijo}el Registro del SII tiene ${r.encontradas} cesión${r.encontradas !== 1 ? 'es' : ''} de estos documentos.` }
          : { ok: false, texto: prefijo + (r.detalle || 'el Registro todavía no tiene anotada esta cesión.') },
      )
    } catch (e: any) {
      setSiiMsg({
        ok: false,
        texto: e?.response?.data?.error?.message || 'No se pudo consultar al SII.',
      })
    }
  }

  const handleCertificado = async () => {
    setCertError(null)
    setBajandoCert(true)
    try {
      const res = await apiClient.get(
        `/treasury/factoring-operations/${operationId}/certificado-sii/`,
        { responseType: 'blob' },
      )
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `certificado_cesion_SII_${op?.date ?? ''}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      // El error viene como blob cuando el backend responde JSON con responseType blob.
      let msg = 'No se pudo obtener el certificado del SII.'
      try {
        const txt = await e?.response?.data?.text?.()
        if (txt) msg = JSON.parse(txt)?.error?.message || msg
      } catch { /* se queda el mensaje genérico */ }
      setCertError(msg)
    } finally {
      setBajandoCert(false)
    }
  }

  const handleSubmit = async () => {
    await submit.mutateAsync(operationId)
    await refetch()
  }

  if (isLoading || !op) {
    return (
      <div className="flex items-center justify-center py-8 text-content-subtle">
        <Loader2 size={18} className="animate-spin mr-2" />Cargando...
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-content">Detalle — {fmtDate(op.date, locale)}</h3>
            <StatusBadge status={op.status} />
            <CesionBadge
              status={op.cesion_status}
              label={op.cesion_status_label}
              trackId={op.sii_track_id}
            />
          </div>
          <p className="text-xs text-content-muted mt-0.5">{op.entity_name}</p>
          {op.created_by_name && (
            <p className="text-[11px] text-content-subtle mt-0.5">
              Creada por {op.created_by_name} · {fmtDate(op.created_at, locale)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {op.cesion_xml && (
            <button
              onClick={() => setShowAec(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-divider text-content-muted text-xs rounded-lg hover:bg-soft"
            >
              <FileCode size={12} />
              Ver AEC
            </button>
          )}
          {op.status === 'created' && (
            <button
              onClick={handleSubmit}
              disabled={submit.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-on-primary text-xs rounded-lg hover:bg-primary-hover disabled:opacity-50"
            >
              <Send size={12} />
              {submit.isPending ? 'Enviando...' : 'Enviar a Procesamiento'}
            </button>
          )}
          {op.status === 'accepted' && !op.payment && (
            <button
              onClick={() => setShowPayment(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success-fill text-white text-xs rounded-lg hover:bg-success-fill-hover"
            >
              <CreditCard size={12} />
              Registrar Pago
            </button>
          )}
          <button onClick={onClose} className="text-xs text-content-subtle hover:text-content-muted">
            Cerrar
          </button>
        </div>
      </div>

      {/* Lines table */}
      <div className="bg-surface-muted rounded-xl border border-divider overflow-hidden mb-4">
        <div className="px-3 py-2 border-b border-divider text-xs font-semibold text-content-muted uppercase tracking-wide">
          Documentos ({op.lines.length})
        </div>
        {op.lines.length === 0 ? (
          <p className="text-xs text-content-subtle text-center py-6">Sin documentos</p>
        ) : (
          <DataTable
            columns={[
              { key: 'documento', header: 'Documento', render: l => <span className="font-mono text-content">{docLabel(l)}</span> },
              { key: 'customer_name', header: 'Cliente', render: l => l.customer_name ?? '—' },
              { key: 'amount_ceded', header: 'Monto Cedido', numeric: true, render: l => fmtNum(l.amount_ceded) },
            ]}
            rows={op.lines}
            rowKey={l => l.id}
            footer={{ amount_ceded: fmtNum(op.total_amount_ceded) }}
            footerLabel="TOTAL CEDIDO"
            // Ya vive dentro del panel del detalle de la operación.
            surface={false}
            minWidth={420}
            caption="Documentos cedidos en la operación"
          />
        )}
      </div>

      {/* Cesión electrónica (SII) — rechazada / aceptada / esperando / sin enviar.
          Se decide por `cesion_status` (derivado en el backend) y NO por la mera
          existencia de `sii_track_id`: un RECHAZO también trae track_id, así que
          esa condición pintaba de verde «Cesión registrada» una cesión que el SII
          había rechazado. */}
      {(op.status === 'accepted' || op.status === 'paid') && (
        op.cesion_status === 'rejected' ? (
          <div className="bg-danger-surface rounded-xl border border-danger-border p-4 mb-4">
            <div className="flex items-start gap-2">
              <XCircle size={15} className="text-danger-content-soft mt-0.5 shrink-0" />
              <div className="text-xs flex-1">
                <p className="font-semibold text-danger-content-strong">
                  El SII RECHAZÓ esta cesión — no está registrada
                </p>
                <p className="text-danger-content mt-1 leading-relaxed">
                  La factura sigue siendo legalmente cobrable por la empresa y no por el
                  factor, aunque el factor ya haya anticipado el dinero.
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-danger-content">
                  <div className="col-span-2"><span className="opacity-70">Motivo:</span> {op.sii_estado}</div>
                  <div><span className="opacity-70">Identificador de envío:</span> <span className="font-mono">{op.sii_track_id}</span></div>
                  {op.sii_received_at && (
                    <div><span className="opacity-70">Fecha:</span> {fmtDate(op.sii_received_at, locale)}</div>
                  )}
                </div>
                <p className="text-danger-content mt-2 leading-relaxed">
                  Si el motivo menciona un error de <strong>schema</strong>, casi siempre falta
                  un dato obligatorio: el <strong>correo</strong> y la <strong>dirección</strong> de
                  la empresa (Configuración → Perfil) o de la entidad de factoring
                  (Tesorería → Entidades). Complétalo y vuelve a enviar.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={handleSendCesion}
                    disabled={sendCesion.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
                  >
                    <Send size={12} /> {sendCesion.isPending ? 'Reenviando…' : 'Reenviar al SII'}
                  </button>
                  {op.cesion_xml && (
                    <button
                      onClick={() => setShowAec(true)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-danger-border-strong rounded-lg text-danger-content-strong hover:bg-danger-surface-strong"
                    >
                      <FileCode size={12} /> Ver AEC
                    </button>
                  )}
                  <button
                    onClick={handleConsultarSii}
                    disabled={consultarSii.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-danger-border-strong rounded-lg text-danger-content-strong hover:bg-danger-surface-strong disabled:opacity-50"
                  >
                    <RefreshCw size={12} className={consultarSii.isPending ? 'animate-spin' : ''} />
                    Consultar estado en el SII
                  </button>
                  <button
                    onClick={() => setShowSiiResult(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-danger-border-strong rounded-lg text-danger-content-strong hover:bg-danger-surface-strong"
                  >
                    Registrar resultado SII
                  </button>
                </div>
                {siiMsg && (
                  <p className={`mt-2 ${siiMsg.ok ? 'text-success-content' : 'text-danger-content'}`}>
                    {siiMsg.texto}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : op.cesion_status === 'pending' ? (
          <div className="bg-warning-surface rounded-xl border border-warning-border p-4 mb-4">
            <div className="flex items-start gap-2">
              <Clock size={15} className="text-warning-content-soft mt-0.5 shrink-0" />
              <div className="text-xs flex-1">
                <p className="font-semibold text-warning-content-strong">
                  Enviada al SII — esperando la respuesta
                </p>
                <p className="text-warning-content mt-1 leading-relaxed">
                  El SII <strong>recibió</strong> el archivo, que no es lo mismo que haberlo
                  aceptado. El resultado llega por correo en unos minutos y puede ser un
                  rechazo. La cesión todavía <strong>no está registrada</strong> en el RPETC.
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-warning-content">
                  <div><span className="opacity-70">Identificador de envío:</span> <span className="font-mono">{op.sii_track_id}</span></div>
                  {op.cesion_sent_at && (
                    <div><span className="opacity-70">Enviado:</span> {fmtDate(op.cesion_sent_at, locale)}</div>
                  )}
                  {op.sii_estado && (
                    <div className="col-span-2"><span className="opacity-70">Último estado:</span> {op.sii_estado}</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={handleConsultarSii}
                    disabled={consultarSii.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
                  >
                    <RefreshCw size={12} className={consultarSii.isPending ? 'animate-spin' : ''} />
                    {consultarSii.isPending ? 'Consultando al SII…' : 'Consultar estado en el SII'}
                  </button>
                  {op.cesion_xml && (
                    <button
                      onClick={() => setShowAec(true)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-warning-border-strong rounded-lg text-warning-content-strong hover:bg-warning-surface-strong"
                    >
                      <FileCode size={12} /> Ver / descargar AEC
                    </button>
                  )}
                  <button
                    onClick={() => setShowSiiResult(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-warning-border-strong rounded-lg text-warning-content-strong hover:bg-warning-surface-strong"
                  >
                    Registrar resultado SII
                  </button>
                </div>
                {siiMsg && (
                  <p className={`mt-2 ${siiMsg.ok ? 'text-success-content' : 'text-warning-content-strong'}`}>
                    {siiMsg.texto}
                  </p>
                )}
                <p className="text-warning-content mt-2 text-[11px]">
                  Clobi consulta al Registro automáticamente 10 minutos después del envío.
                </p>
              </div>
            </div>
          </div>
        ) : op.cesion_status === 'accepted' ? (
          <div className="bg-success-surface rounded-xl border border-success-border p-4 mb-4">
            <div className="flex items-start gap-2">
              <CheckCircle2 size={15} className="text-success-content-soft mt-0.5 shrink-0" />
              <div className="text-xs flex-1">
                <p className="font-semibold text-success-content-strong">
                  Cesión registrada en el RPETC del SII
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-success-content">
                  <div><span className="opacity-70">Estado:</span> {op.sii_estado || 'Aceptada'}</div>
                  <div><span className="opacity-70">Identificador de envío:</span> <span className="font-mono">{op.sii_track_id}</span></div>
                  {op.sii_received_at && (
                    <div><span className="opacity-70">Fecha de recepción:</span> {fmtDate(op.sii_received_at, locale)}</div>
                  )}
                  {op.lines.map(l => l.identificador_cesion && (
                    <div key={l.id}><span className="opacity-70">Id. cesión {docLabel(l)}:</span> <span className="font-mono">{l.identificador_cesion}</span></div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={handleCertificado}
                    disabled={bajandoCert}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-success-fill text-white rounded-lg hover:bg-success-fill-hover disabled:opacity-50"
                  >
                    <Download size={12} />
                    {bajandoCert ? 'Pidiéndolo al SII…' : 'Descargar certificado del SII'}
                  </button>
                  {op.cesion_xml && (
                    <button
                      onClick={() => setShowAec(true)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-emerald-300 dark:border-emerald-700 rounded-lg text-success-content-strong hover:bg-success-surface-strong"
                    >
                      <FileCode size={12} /> Ver / descargar AEC
                    </button>
                  )}
                  <button
                    onClick={handleConsultarSii}
                    disabled={consultarSii.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-emerald-300 dark:border-emerald-700 rounded-lg text-success-content-strong hover:bg-success-surface-strong disabled:opacity-50"
                  >
                    <RefreshCw size={12} className={consultarSii.isPending ? 'animate-spin' : ''} />
                    Revalidar en el SII
                  </button>
                  <button
                    onClick={() => setShowSiiResult(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-emerald-300 dark:border-emerald-700 rounded-lg text-success-content-strong hover:bg-success-surface-strong"
                  >
                    Registrar resultado SII
                  </button>
                </div>
                <p className="text-success-content mt-2 leading-relaxed">
                  El <strong>certificado</strong> es el documento con el que se le prueba al
                  factor o al deudor que la cesión quedó anotada: lo emite el SII con su
                  propio folio.
                </p>
                {certError && (
                  <p className="mt-1 text-danger-content">{certError}</p>
                )}
                {siiMsg && (
                  <p className={`mt-1 ${siiMsg.ok ? 'text-success-content' : 'text-warning-content-strong'}`}>
                    {siiMsg.texto}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : op.cesion_signed ? (
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-200 dark:border-primary-800 p-4 mb-4">
            <div className="flex items-start gap-2">
              <FileCode size={15} className="text-primary mt-0.5 shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-content">Cesión electrónica (SII)</p>
                <p className="text-content-muted mt-0.5 leading-relaxed">
                  AEC <strong>firmado</strong> con el certificado de la empresa — válido para el RPETC del SII.
                  Envíalo directamente con <strong>«Enviar al SII»</strong>, o descarga el AEC firmado
                  para subirlo al portal del SII (RPETC).
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={handleSendCesion}
                    disabled={sendCesion.isPending}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
                  >
                    <Send size={12} /> {sendCesion.isPending ? 'Enviando…' : 'Enviar al SII'}
                  </button>
                  <button
                    onClick={() => setShowAec(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-divider rounded-lg text-content-muted hover:bg-soft"
                  >
                    <FileCode size={12} /> Ver / descargar AEC
                  </button>
                  <button
                    onClick={() => setShowSiiResult(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-divider rounded-lg text-content-muted hover:bg-soft"
                  >
                    Registrar resultado SII
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-warning-surface rounded-xl border border-warning-border p-4 mb-4">
            <div className="flex items-start gap-2">
              <FileCode size={15} className="text-warning-content-soft mt-0.5 shrink-0" />
              <div className="text-xs">
                <p className="font-semibold text-warning-content-strong">Cesión electrónica (SII)</p>
                <p className="text-warning-content mt-0.5 leading-relaxed">
                  {op.cesion_xml
                    ? 'El AEC se generó pero NO se pudo firmar (no hay certificado digital configurado en Configuración → Localización). Sin firma no es válido para el SII. Configura el certificado y reintenta la cesión.'
                    : 'No se generó el AEC para esta operación. Revisa los datos de la empresa y de la entidad de factoring.'}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {op.cesion_xml && (
                    <button
                      onClick={() => setShowAec(true)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-warning-border-strong rounded-lg text-warning-content-strong hover:bg-warning-surface-strong"
                    >
                      <FileCode size={12} /> Ver AEC (sin firmar)
                    </button>
                  )}
                  <button
                    onClick={() => setShowSiiResult(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-warning-border-strong rounded-lg text-warning-content-strong hover:bg-warning-surface-strong"
                  >
                    Registrar resultado SII
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Payment info */}
      {op.payment && (
        <div className="bg-success-surface rounded-xl border border-success-border p-4">
          <p className="text-xs font-semibold text-success-content mb-2">Pago Registrado</p>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div>
              <p className="text-success-content-soft">Fecha</p>
              <p className="font-medium text-success-content-strong">{fmtDate(op.payment.payment_date, locale)}</p>
            </div>
            <div>
              <p className="text-success-content-soft">Monto Neto</p>
              <p className="font-medium text-success-content-strong">{fmtNum(op.payment.amount_received_net)}</p>
            </div>
            <div>
              <p className="text-success-content-soft">Comisiones</p>
              <p className="font-medium text-success-content-strong">{fmtNum(op.payment.commissions_charged)}</p>
            </div>
          </div>
        </div>
      )}

      {showPayment && (
        <RegisterPaymentModal
          operationId={operationId}
          totalCeded={Number(op.total_amount_ceded) || 0}
          onClose={async () => {
            setShowPayment(false)
            await refetch()
          }}
        />
      )}

      {showAec && <AecViewerModal op={op} onClose={() => setShowAec(false)} />}

      {showSiiResult && (
        <SiiResultModal
          operationId={operationId}
          onClose={async () => {
            setShowSiiResult(false)
            await refetch()
          }}
        />
      )}
    </div>
  )
}

// ─── Main Tab ──────────────────────────────────────────────────────────────────

export function FactoringOperationsTab() {
  const locale = useTenantLocale()
  const [showCreate, setShowCreate] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [cesionFilter, setCesionFilter] = useState('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const { can } = usePermission()
  const canCreate = can('treasury.operations', 'create')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  // Cualquier cambio de filtro vuelve a la primera página: si no, filtrar desde la
  // página 3 muestra un listado vacío que parece "no hay resultados".
  useEffect(() => { setPage(1) }, [statusFilter, cesionFilter, debouncedSearch])

  const { data, isLoading, isFetching } = useFactoringOperations({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(cesionFilter ? { cesion_status: cesionFilter } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    page,
    page_size: 25,
  })
  const operations = data?.operations ?? []
  const numPages = data?.num_pages ?? 1
  const hayFiltros = Boolean(statusFilter || cesionFilter || debouncedSearch)

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
            <input aria-label="Buscar operaciones"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Folio, cliente, entidad o N° de envío"
              className="w-64 border border-divider rounded-lg pl-8 pr-8 py-1.5 text-xs text-content bg-surface focus:ring-2 focus:ring-focus-ring"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content"
                aria-label="Limpiar búsqueda"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <select aria-label="Todos los estados"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-divider rounded-lg px-3 py-1.5 text-xs text-content-muted bg-surface focus:ring-2 focus:ring-focus-ring"
          >
            <option value="">Todos los estados</option>
            <option value="created">Creada</option>
            <option value="processing">Procesando</option>
            <option value="accepted">Cedida</option>
            <option value="rejected">Rechazada</option>
            <option value="paid">Liquidada</option>
          </select>
          <select aria-label="Cesión SII: todas"
            value={cesionFilter}
            onChange={e => setCesionFilter(e.target.value)}
            className="border border-divider rounded-lg px-3 py-1.5 text-xs text-content-muted bg-surface focus:ring-2 focus:ring-focus-ring"
          >
            <option value="">Cesión SII: todas</option>
            <option value="not_sent">Sin enviar al SII</option>
            <option value="pending">Esperando al SII</option>
            <option value="accepted">Aceptada por el SII</option>
            <option value="rejected">Rechazada por el SII</option>
          </select>
          <span className="text-xs text-content-subtle">
            {data ? `${data.count} operación${data.count !== 1 ? 'es' : ''}` : ''}
            {isFetching && !isLoading ? ' · actualizando…' : ''}
          </span>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
          >
            <Plus size={14} />
            Nueva Operación
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Cargando...
        </div>
      ) : operations.length === 0 ? (
        <div className="text-center py-16 text-content-subtle">
          <FileText size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">
            {hayFiltros
              ? 'Ninguna operación coincide con los filtros.'
              : 'No hay operaciones de factoring.'}
          </p>
        </div>
      ) : (
        <>
          <DataTable
            columns={[
              { key: 'date', header: 'Fecha', render: (op: FactoringOperationListItem) => <span className="text-xs whitespace-nowrap">{fmtDate(op.date, locale)}</span> },
              { key: 'entity_name', header: 'Entidad', render: (op: FactoringOperationListItem) => <span className="font-medium text-content">{op.entity_name}</span> },
              {
                key: 'documents', header: 'Documentos cedidos',
                render: (op: FactoringOperationListItem) => <DocumentsCell documents={op.documents} lineCount={op.line_count} />,
              },
              {
                key: 'total_amount_ceded', header: 'Monto cedido', numeric: true,
                sortValue: (op: FactoringOperationListItem) => Number(op.total_amount_ceded),
                render: (op: FactoringOperationListItem) => <span className="font-semibold text-content">{fmtNum(op.total_amount_ceded)}</span>,
              },
              { key: 'status', header: 'Estado', render: (op: FactoringOperationListItem) => <StatusBadge status={op.status} /> },
              {
                key: 'cesion_status', header: 'Cesión SII',
                render: (op: FactoringOperationListItem) => (
                  <CesionBadge status={op.cesion_status} label={op.cesion_status_label} trackId={op.sii_track_id} />
                ),
              },
            ]}
            rows={operations}
            rowKey={(op: FactoringOperationListItem) => op.id}
            onRowClick={(op: FactoringOperationListItem) => setDetailId(op.id)}
            minWidth={900}
            caption="Operaciones de factoring"
          />

          {numPages > 1 && (
            <Pagination
              page={page}
              pageSize={200}
              total={data?.count ?? 0}
              onPageChange={setPage}
              locale={locale}
            />
          )}
        </>
      )}

      {detailId && (
        <OperationDetailModal operationId={detailId} onClose={() => setDetailId(null)} />
      )}
      {showCreate && <CreateOperationModal onClose={() => setShowCreate(false)} />}
    </div>
  )
}
